import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MessageSquare, Eye, Calendar, ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';

import { BACKEND_URL as API_URL } from '../config';

// Format message content (same as Dashboard)
const formatMessage = (content) => {
  if (!content) return '';
  return content
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code class="bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>')
    .replace(/\n\n/g, '</p><p class="mb-3">')
    .replace(/\n/g, '<br/>');
};

export default function SharedConversationPage() {
  const { shareId } = useParams();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSharedConversation = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/share/${shareId}`);
        setConversation(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Udostępniona rozmowa nie została znaleziona.');
        } else if (err.response?.status === 410) {
          setError('Link do tej rozmowy wygasł.');
        } else {
          setError('Wystąpił błąd podczas ładowania rozmowy.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSharedConversation();
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-orange-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-400">Ładowanie rozmowy...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-orange-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="font-heading text-2xl text-slate-900 dark:text-slate-100 mb-2">Nie znaleziono</h1>
          <p className="text-slate-600 dark:text-slate-400 mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-primary-500 hover:bg-primary-600 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Strona główna
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 lg:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1e3a5f] dark:bg-[#3b6a9e] flex items-center justify-center overflow-hidden">
              <img src="/kai-logo.png" alt="KAI" className="w-12 h-12 object-contain rounded-lg" />
            </div>
            <div>
              <h1 className="font-heading text-lg text-[#1e3a5f] dark:text-slate-100 font-semibold">
                {conversation.title}
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">Udostępniona rozmowa z KAI</p>
            </div>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm" className="dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4 mr-1" />
              KAI
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-4xl mx-auto px-4 lg:px-6 py-4">
        <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-1">
            <Eye className="w-4 h-4" />
            <span>{conversation.view_count} wyświetleń</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>{new Date(conversation.created_at).toLocaleDateString('pl-PL')}</span>
          </div>
          <div className="flex items-center gap-1">
            <MessageSquare className="w-4 h-4" />
            <span>{conversation.messages.length} wiadomości</span>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="max-w-4xl mx-auto px-4 lg:px-6 pb-12">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-100 dark:border-slate-700 overflow-hidden">
          <ScrollArea className="p-4 lg:p-6">
            <div className="space-y-4 lg:space-y-6">
              {conversation.messages.map((message, idx) => (
                <div
                  key={idx}
                  className={`flex gap-3 ${
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  {message.role === 'assistant' && (
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 shadow-sm">
                      <img src="/images/kai_avatar.jpeg" alt="KAI" className="w-full h-full object-cover bg-white" />
                    </div>
                  )}
                  <div
                    className={`max-w-[85%] lg:max-w-2xl px-4 py-3 ${
                      message.role === 'user'
                        ? 'bg-[#1e3a5f] dark:bg-[#3b6a9e] text-white rounded-2xl rounded-br-sm'
                        : 'bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-600 rounded-2xl rounded-bl-sm'
                    }`}
                  >
                    {message.role === 'user' ? (
                      <p className="text-sm leading-relaxed">{message.content}</p>
                    ) : (
                      <div 
                        className="text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none"
                        dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
                      />
                    )}
                  </div>
                  {message.role === 'user' && (
                    <div className="w-9 h-9 rounded-md bg-slate-200 dark:bg-slate-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-slate-600 dark:text-slate-300 text-sm font-medium">U</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-t border-slate-200 dark:border-slate-700 p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Chcesz rozmawiać z KAI? Załóż bezpłatne konto!
          </p>
          <Link to="/auth">
            <Button className="bg-[#f5a623] hover:bg-[#d98e15] text-white">
              Rozpocznij za darmo
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
